import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Eye, EyeOff, ArrowLeft, ArrowRight, Loader2, Mail } from 'lucide-react';
import { signIn } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import MiniMatrixPreview from '@/components/auth/MiniMatrixPreview';
import MiniAIAssistantPreview from '@/components/auth/MiniAIAssistantPreview';
import { PageTransition } from '@/components/PageTransition';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  usePageTitle('Sign In');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Removed auto-rotate for better user control

  const carouselSlides = [
    {
      title: "AI Compliance Assistant",
      description: "Your personal AI assistant handles bookings, recommendations, and compliance queries.",
      component: MiniAIAssistantPreview,
      gradient: "from-violet-500 to-purple-600"
    },
    {
      title: "Visual Compliance Matrix",
      description: "Track your entire team's training status in one powerful, real-time dashboard.",
      component: MiniMatrixPreview,
      gradient: "from-emerald-500 to-teal-600"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowResendConfirmation(false);
    setResendSuccess(false);
    setIsLoading(true);

    try {
      const { data, error: signInError } = await signIn(
        formData.email,
        formData.password
      );

      if (signInError) {
        setError(signInError.message);
        // Check if the error is about unconfirmed email
        if (signInError.message.toLowerCase().includes('email not confirmed') ||
            signInError.message.toLowerCase().includes('email is not confirmed') ||
            signInError.message.toLowerCase().includes('confirm your email')) {
          setShowResendConfirmation(true);
        }
      } else {
        console.log('Login successful, AuthContext will handle the rest');
        // Check for redirect parameter
        const redirectTo = searchParams.get('redirect');
        if (redirectTo) {
          // Navigate to the redirect URL
          navigate(redirectTo);
        }
        // Otherwise, let AuthContext/PublicRoute handle default redirect
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    setError('');

    try {
      // Call an edge function to handle the resend without triggering Supabase email
      const { data, error } = await supabase.functions.invoke('resend-confirmation', {
        body: {
          email: formData.email
        }
      });

      if (error) {
        // If edge function doesn't exist or fails, show error
        setError('Failed to resend confirmation email. Please contact support.');
        console.error('Resend confirmation error:', error);
      } else if (data?.error) {
        setError(data.error);
      } else {
        setResendSuccess(true);
        setShowResendConfirmation(false);
        setError('');
        // Show success message for a few seconds
        setTimeout(() => {
          setResendSuccess(false);
        }, 5000);
      }
    } catch (err) {
      setError('Failed to resend confirmation email. Please try again.');
      console.error('Resend error:', err);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <PageTransition>
    <div className="min-h-screen flex">
      {/* Left Panel - Carousel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        {/* Dynamic Gradient Background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${carouselSlides[currentSlide].gradient} opacity-20 transition-all duration-1000`} />

        {/* Carousel Content */}
        <div className="relative z-10 flex flex-col w-full h-full">
          {/* Top Section with Text */}
          <div className="px-12 pt-12 pb-6 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              {carouselSlides[currentSlide].title}
            </h1>
            <p className="text-lg text-gray-300 max-w-md mx-auto">
              {carouselSlides[currentSlide].description}
            </p>
          </div>

          {/* Component Preview Area */}
          <div className="flex-1 relative">
            {carouselSlides.map((slide, index) => {
              const PreviewComponent = slide.component;
              return (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-500 ${
                    index === currentSlide
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-95 pointer-events-none'
                  }`}
                >
                  <PreviewComponent isActive={index === currentSlide} />
                </div>
              );
            })}
          </div>

          {/* Bottom Navigation */}
          <div className="px-12 py-8 flex items-center justify-between">
            {/* Slide Indicators */}
            <div className="flex space-x-2">
              {carouselSlides.map((_, index) => (
                <button
                  key={index}
                  className={`h-2 transition-all duration-300 rounded-full ${
                    index === currentSlide
                      ? 'w-8 bg-white'
                      : 'w-2 bg-white/30 hover:bg-white/50'
                  }`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={prevSlide}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={nextSlide}
                className="text-white hover:bg-white/10"
              >
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-none">
            <CardHeader className="text-center pb-8">
              <div className="mb-6">
                <img
                  src="https://storage.googleapis.com/trainingmatrix/images/tmlogoiconv2.png"
                  alt="Ai Skills Matrix"
                  className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-xl drop-shadow-md"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">
                Welcome back
              </CardTitle>
              <CardDescription className="text-slate-600 mt-2">
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                {showResendConfirmation && (
                  <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-md">
                    <p className="text-sm text-amber-800 mb-3">
                      Your email address hasn't been confirmed yet. Would you like us to resend the confirmation email?
                    </p>
                    <Button
                      type="button"
                      onClick={handleResendConfirmation}
                      disabled={resendLoading}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      {resendLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Resend Confirmation Email
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {resendSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                    ✓ Confirmation email sent! Please check your inbox and click the link to verify your account.
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="h-11"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="h-11 pr-10"
                      required
                      disabled={isLoading}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-11 px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-500" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember"
                      checked={rememberMe}
                      onCheckedChange={setRememberMe}
                    />
                    <Label
                      htmlFor="remember"
                      className="text-sm text-slate-600 cursor-pointer"
                    >
                      Remember me
                    </Label>
                  </div>
                  
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 text-sm text-slate-900 hover:text-slate-700"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot password?
                  </Button>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>

              </form>
            </CardContent>

            <CardFooter className="justify-center pt-6">
              <p className="text-sm text-slate-600">
                Don't have an account?{' '}
                <Button
                  type="button"
                  variant="link"
                  className="px-0 text-slate-900 hover:text-slate-700"
                  onClick={() => navigate('/signup')}
                >
                  Sign up
                </Button>
              </p>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
    </PageTransition>
  );
};

export default LoginPage;